// Library import
import {useEffect, useRef, useState} from 'react';
import {Form, Alert} from 'react-bootstrap';
import Proptypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// File Import
import uploadIcon from '~/images/icon/upload.png';
import CrossIcon from '~/images/icon/cross-gray.png';
import PlusIcon from '~/images/icon/square-plus.png';
import MiniCross from '~/images/icon/mini-cross.png';
import LoadingGif from '~/images/common/loading.gif';
import {caseStudy, caseStudyItemTitle} from '@/constant';
import {TagApi, PostApi} from '@/http';
import {getTagsBasedOnGroup} from '@/config/mixins';
import { formatDate } from '@/config';

// Components Import
import {ScheduleModal, AddNewSection, PostPreviewModal} from '@/components';

const initialFormData = {
    title: '',
    image: '',
    url: '',
    tags: [],
    notification_flg: 1,
    user_name: '',
};

const initialItem = [
    {
        title: caseStudy[0].value,
        sub_items: [
            {
                title: caseStudyItemTitle[0],
                description: '',
                image: ''
            }
        ]
    }
];

export const MultiPostForm = ({postData = null}) => {
    const url = new URL(window.location.href);
    const [typeParam, setTypeParam] = useState(null);
    const [tags, setTags] = useState([]);
    const [loadMore, setLoadMore] = useState(false);
    const [formData, setFormData] = useState(initialFormData || {});
    const [itemTitles, setItemTitles] = useState(caseStudy || []);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [postPreviewModal, setPostPreviewModal] = useState(false);
    const [items, setItems] = useState([]);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [postStatus, setPostStatus] = useState('');
    const [errorStatus, setErrorStatus] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [publishedDate, setPublishedDate] = useState(new Date());
    const [isDisabled, setIsDisabled] = useState(true);
    const [ready, setReady] = useState(false);
    const [isRemovePost, setIsRemovePost] = useState(false);
    // const [userName, setUserName] = useState('');
    const fileUploadRef = useRef(null);

    useEffect(() => {
        setTypeParam(`${url.searchParams.get('type')}`);
        if (postData) {
            setFormData({
                ...formData,
                title: postData?.title ? postData?.title : '',
                id: postData?.id,
                image: postData?.original_image ? postData?.original_image : '',
                original_image: postData?.image,
                url: postData?.url ? postData?.url : '',
                status: postData?.status,
                scheduled_at: postData?.scheduled_at,
                tags: getTagsBasedOnGroup(postData?.tags),
                notification_flg: postData?.notification_flg,
                expired_at: postData?.expired_at,
                user_name: postData?.user_name ? postData?.user_name : '',
            });
            setItems([...postData.items]);
            setOriginalImage(postData?.image);
            setIsDisabled(false);
        } else {
            setFormData({
                ...formData,
                title: '「支援メニュー名●●」の利用事例'
            });
            setItems(initialItem);
        }

        if (postData?.published_at) {
          setPublishedDate(new Date(postData?.published_at));
        }

        setReady(true);
        // setUserName(handleGetUsername());
    }, []);

    useEffect(() => {
        if (typeParam) fetchTags();
    }, [typeParam]);

    useEffect(() => {
        if (!ready) return;
        handleCheckRequired();
    }, [formData, items, tags, originalImage, ready]);

    useEffect(() => {
        if (!ready || isRemovePost || postData?.id) return;
        if (items?.length < caseStudy?.length) {
            handleAddNewSection();
        } else {
            const copyItems = [...items];
            const itemIndex = 2;
            const subItemLength = copyItems[itemIndex].sub_items.length;
            if (subItemLength < 3) {
                handleAddNewPost(itemIndex);
            }
        }
        
    }, [items]);

    const fetchTags = async () => {
        try {
            const response = await TagApi.getTags(typeParam);

            const data = getTagsBasedOnGroup(response.data.data);

            if (postData?.tags?.length) {
                data?.map((value, index) =>
                    postData?.tags?.some((oneElement) => {
                        if (oneElement.id === value.id) {
                            setTags([...tags, (data[index] = {...value, selected: true})]);
                        }
                        setTags(data);
                    })
                );
            } else {
                setTags(data);
            }
        } catch (error) {
            setServerError('Server error occured !');
        }
    };

    /**
     * Open upload file
     * @param {*} event 
     * @returns 
     */
    const handleFileChange = (event) => {
        event.preventDefault();
        const { files } = event.currentTarget;
        if (!files || !files[0]) return;
        handleUploadImage(files);
    }

    /**
     * Drop upload file
     * @param {*} event 
     */
    const handleFileDrop = (event) => {
        event.preventDefault();
        if ((!event.dataTransfer) || (!event.dataTransfer.files || !event.dataTransfer.files[0])) return;
        handleUploadImage(event.dataTransfer.files);
    }

    // Upload Image API handle
    const handleUploadImage = async (fileData) => {
        const formData = new FormData();

        if (fileData) formData.append('image', fileData[0]);
        try {
            setIsUploading(true);
            setErrors({...errors, image: ''});
            const response = await PostApi.uploadImage(formData);
            setOriginalImage(response.data.data.original_url);
            if (response.status === 200) {
                setErrors({...errors, image: ''});
                setTimeout(() => {
                    setIsUploading(false);
                }, 1000);
                setOriginalImage(response.data.data.original_url);
                handleChange({
                    target: {
                        name: 'image',
                        value: response.data.data.image_url
                    }
                });
            }
        } catch (error) {
            setIsUploading(false);
            if (error.response.status === 422) {
                setErrors({...errors, image: error.response.data.message});
            } else {
                setErrors({...errors, image: 'Server error occured !'});
            }
        }
    };

    // Get image full path
    const getImageFullPath = (value, itemIndex, postIndex) => {
        const copyItems = [...items];

        copyItems[itemIndex].sub_items[postIndex].image_full_path = value;
        setItems(copyItems);
    };

    // Handle Add New Post
    const handleAddNewSection = () => {
        const itemIndex = items?.length ? items?.length : 0;
        const itemTitle = postData?.id ? '' : caseStudy[itemIndex].value;
        const subItemTitle = postData?.id ? '' : caseStudyItemTitle[itemIndex];
        const emptyItems = {
            title: itemTitle,
            sub_items: [
                {
                    title: subItemTitle,
                    description: '',
                    image: ''
                }
            ]
        };
        setItems((prevProps) => [...prevProps, emptyItems]);
    };

    // Handle Add New Post
    const handleAddNewPost = (itemIndex) => {
        const copyItems = [...items];
        let subItemTitle = '';
        if (itemIndex > 1 && !postData?.id) {
            const subItemIndex = copyItems[itemIndex].sub_items.length;
            subItemTitle = subItemIndex == 1 ? caseStudyItemTitle[3] : caseStudyItemTitle[4];
        }

        copyItems[itemIndex].sub_items.push({
            title: subItemTitle,
            description: '',
            image: ''
        });

        setItems(copyItems);
    };

    /* Handle remove post */
    /**
     * Post remove if clicked on post  cross icon
     * Section remove if clicked on  section cross icon
     * Remove action fired based on index number availibity
     */
    const handleRemovePost = (itemIndex = null, postIndex = null) => {
        const copyItems = [...items];
        const copyTitles = [...itemTitles];

        if (postIndex !== null) {
            const sub_items = copyItems[itemIndex].sub_items;

            if (sub_items?.length > 1) {
                const updateItems = sub_items?.filter((item, index) => index !== postIndex ?? item);

                copyItems[itemIndex].sub_items = updateItems;
                setItems(copyItems);
            }
        } else {
            if (copyItems?.length > 1) {
                const updateArr = copyTitles?.map((item) => {
                    if (item.value === items[itemIndex]?.title) {
                        return {
                            ...item,
                            selected: false
                        };
                    }

                    return item;
                });
                setItemTitles(updateArr);

                const updateItems = copyItems?.filter((item, index) => index !== itemIndex ?? item);

                setItems(updateItems);
            }
        }
        setIsRemovePost(true);
    };

    // Handle select tag
    const handleSelectTag = (tag) => {
        const filterArr = tags?.map((item) => {
            if (item.id === tag.id) return {...item, selected: true};
            return item;
        });

        setFormData({...formData, tags: [...formData.tags, tag]});
        setTags(filterArr);
        if (formData?.tags?.length) {
            setErrors({...errors, tags: 'タグを設定してください'});
        } else {
            setErrors({...errors, tags: ''});
        }
    };

    // Handle remove tag
    const handleRemoveTag = (tag) => {
        const filterArr = formData?.tags?.filter((item) => item.id !== tag.id);

        const updateArr = tags?.map((item) => {
            if (item.id === tag.id) return {...item, selected: false};
            return item;
        });

        setTags(updateArr);
        setFormData({...formData, tags: filterArr});

        if (formData?.tags?.length) {
            setErrors({...errors, tags: 'タグを設定してください'});
        } else {
            setErrors({...errors, tags: ''});
        }
    };

    /* Handle change event */
    /**
     * Handle normal formadata on change event, which update formdata
     * Hadle section on change event, which update section inputs
     * Handle post on change event, which update post inputs
     */
    const handleChange = (event, itemIndex = null, postIndex = null) => {
        const copyItems = [...items];

        // Formdata handle change
        if (itemIndex === null && postIndex === null) {
            setFormData({...formData, [event.target.name]: event.target.value});

            if (errorStatus) {
                setErrors({...errors, [event.target.name]: ''});
            }
        }
        // Multiple section change handle
        if (itemIndex !== null && postIndex !== null) {
            if (errorStatus) {
                let updateError = {...errors};

                if (updateError.items?.[itemIndex]?.sub_items?.[postIndex]?.[event.target.name] !== undefined) {
                    updateError.items[itemIndex].sub_items[postIndex][event.target.name] = '';
                }

                setErrors(updateError);
            }

            if (event.target.name === 'image' && event.target.value == '') {
                delete copyItems[itemIndex].sub_items[postIndex]['image_full_path'];
            }
            copyItems[itemIndex].sub_items[postIndex][event.target.name] = event.target.value;
            setItems(copyItems);
        } else if (itemIndex !== null && postIndex === null) {
            if (event.target.name === 'title') handleSectionOption(event.target.value);

            if (errorStatus) {
                let updateError = {...errors};

                if (updateError.items?.[itemIndex]?.[event.target.name] !== undefined) updateError.items[itemIndex][event.target.name] = '';

                setErrors(updateError);
            }

            copyItems[itemIndex][event.target.name] = event.target.value;
            setItems(copyItems);
        }
    };

    // Handle Section Option
    const handleSectionOption = (option) => {
        const copyTitles = [...itemTitles];

        const updateArr = copyTitles?.map((item) => {
            if (item.value === option) {
                return {
                    ...item,
                    selected: true
                };
            }

            return item;
        });
        setItemTitles(updateArr);
    };

    // Form validation final codes
    const findFormErrors = (checkType) => {
        const {title, image, url, tags, user_name} = formData;
        const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;

        const newErrors = {items: []};

        let hasError = false;

        // name errors
        if ((!title || title === '') && (checkType === 'all' || checkType === 'required')) {
            newErrors.title = '記事タイトルを入力してください';
            hasError = true;
        } else if (title && title.length < 2 && (checkType === 'all' || checkType === 'value')) {
            newErrors.title = 'タイトルは 2 文字以上にする必要があります。';
            hasError = true;
        } else if (title && title.length > 256 && (checkType === 'all' || checkType === 'value')) {
            newErrors.title = 'タイトルは 256 文字以下にする必要があります。';
            hasError = true;
        }
        if ((!image || image === '') && (checkType === 'all' || checkType === 'required')) {
            newErrors.image = 'トップイメージをアップロードしてください';
            hasError = true;
        }
        if (url && !urlRegex.test(url) && (checkType === 'all' || checkType === 'value')) {
            newErrors.url = 'URL は有効な URL でなければなりません。';
            hasError = true;
        }
        if ((!tags || !tags?.length) && (checkType === 'all' || checkType === 'required')) {
            newErrors.tags = 'タグを設定してください';
            hasError = true;
        }
        if ((!user_name || user_name === '') && (checkType === 'all' || checkType === 'required')) {
          newErrors.user_name = '担当者名を入力してください';
          hasError = true;
        }

        const getErrorMsg = (key, input) => {
            if (input === 'title') {
                if ((!key || key === '') && (checkType === 'all' || checkType === 'required')) {
                    hasError = true;
                    return 'コンテンツタイトルを選択してください';
                } else if (key && key.length < 2 && (checkType === 'all' || checkType === 'value')) {
                    hasError = true;
                    return 'タイトルは 2 文字以上にする必要があります。';
                } else if (key && key.length > 1000 && (checkType === 'all' || checkType === 'value')) {
                    hasError = true;
                    return 'タイトルは 1000 文字以下にする必要があります。';
                }
            }

            if (input === 'itemTitle') {
                if ((!key || key === '') && (checkType === 'all' || checkType === 'required')) {
                    hasError = true;
                    return 'セクションタイトルを選択してください';
                }
            }

            if (input === 'description') {
                if ((!key || key === '') && (checkType === 'all' || checkType === 'required')) {
                    hasError = true;
                    return 'コンテンツ本文を入力してください';
                } else if (key && key.length < 2 && (checkType === 'all' || checkType === 'value')) {
                    hasError = true;
                    return '説明は 2 文字以上にする必要があります。';
                } else if (key && key.length > 1000 && (checkType === 'all' || checkType === 'value')) {
                    hasError = true;
                    return '説明は 1000 文字以下にする必要があります。';
                }
            }

            if (input === 'image') {
                if ((!key || key === '') && (checkType === 'all' || checkType === 'required')) {
                    hasError = true;
                    return 'イメージをアップロードしてください';
                }
            }

            return '';
        };

        items?.forEach((item, index) => {
            newErrors.items[index] = {
                title: getErrorMsg(items[index].title, 'itemTitle'),
                sub_items: items[index].sub_items.map((content, idx) => {
                    return {
                        title: getErrorMsg(items[index].sub_items[idx].title, 'title'),
                        description: getErrorMsg(items[index].sub_items[idx].description, 'description'),
                        image: getErrorMsg(items[index].sub_items[idx].image, 'image')
                    };
                })
            };
        });

        setErrorStatus(hasError);

        return {newErrors, hasError};
    };

    // Handle Post Submit
    const handlePostPublish = (event, actionType) => {
        const {newErrors, hasError} = findFormErrors('all');

        if (hasError) {
            // We got errors!
            setErrors(newErrors);
            if (actionType === 'preview') {
                setPostPreviewModal(true);
                setErrorStatus(false)
            }
        } else {
            const published_at = `${formatDate(publishedDate, 'YYYY-MM-DD')}`;
            setFormData({...formData, items, type: typeParam, published_at: published_at});
            if (actionType === 'submit') {
                setShowScheduleModal(true);
            } else if (actionType === 'preview') {
                setPostPreviewModal(true);
            }
        }
    };

    // Handle remove image
    const handleRemoveImage = (event) => {
        event.stopPropagation();
        setOriginalImage(null);
        handleChange({
            target: {
                name: 'image',
                value: ''
            }
        });
    };

    const handleCheckboxChange = (event) => {
        handleChange({
            target: {
                name: event.target.name,
                value: event.target.checked ? 1 : 0
            }
        });
    };

    // Handle draft Post Submit
    const handleDraftPostSubmit = (event) => {
        setErrors({});
        const {newErrors, hasError} = findFormErrors('value');

        if (hasError) {
            // We got errors!
            setErrors(newErrors);
        } else {
            const published_at = `${formatDate(publishedDate, 'YYYY-MM-DD')}`;
            setFormData({...formData, items, type: typeParam, published_at: published_at});
            setShowScheduleModal(true);
        }
    };

    const handleCheckRequired = () => {
        setErrors({});
        const { newErrors, hasError } = findFormErrors('required');

        if (hasError) {
          setIsDisabled(true);
          setErrors(newErrors);
        } else {
          setIsDisabled(false);
        }
    };

    const handleGetUsername = () => {
        let element = document.getElementById('Header');
        let userName = '';
        if (element) {
          const currentUser = element?.dataset?.user && JSON.parse(element.dataset.user);
          userName = currentUser.name
        }
        return userName;
    };

    return (
        <div className='page-wrapper bg-gray px-4'>
            <Form className='form-area' onSubmit={(event) => event.preventDefault()}>
                {/* Header title */}
                <Form.Group className='mb-3' controlId='title'>
                    <Form.Label>
                        タイトル <span className='is-required'>*</span>
                    </Form.Label>
                    <Form.Control
                        type='text'
                        onChange={handleChange}
                        name='title'
                        value={formData?.title}
                        placeholder='19文字以内の記入を推奨します（超過部分が...の表示になる場合があります）'
                        isInvalid={errors?.title}
                    />
                    <Form.Control.Feedback type='invalid'>{errors?.title}</Form.Control.Feedback>
                </Form.Group>

                {/* Header Image */}
                <Form.Group className='mb-4' controlId='uploadMainImage'>
                    <Form.Label>
                        トップイメージアップロード <span className='is-required'>*</span>
                        <span className='is-allowed'>PNG,JPG & GIF files are allowed</span>
                    </Form.Label>

                    <div className={errors?.image ? 'form-file is-invalid' : 'form-file'} onDragOver={handleFileDrop} onDrop={handleFileDrop} onClick={() => fileUploadRef?.current?.click()}>
                        <input id='uploadMainImage' type='file' hidden onChange={(event) => handleFileChange(event)} ref={fileUploadRef} accept='image/png, image/gif, image/jpeg' />
                        <div className='file-content' >
                            {originalImage !== null ? (
                                <div className='preview-content'>
                                    <img src={originalImage} alt='Header Image'/>
                                </div>
                            ) : (
                                <>
                                    <img src={uploadIcon} alt='Upload Icon'/>
                                    <span>クリックして画像を追加</span>
                                </>
                            )}
                        </div>
                        {isUploading && (
                            <div className='upload-image'>
                                <img src={LoadingGif} style={{width: '170px'}} alt='Loading Image'/>
                            </div>
                        )}

                        {/* Remove upload image */}
                        {originalImage !== null && !isUploading && (
                            <div className='remove-item remove-image'>
                                <img onClick={handleRemoveImage} src={CrossIcon} alt='Cross Icon'/>
                            </div>
                        )}
                    </div>
                    <Form.Control.Feedback type='invalid'>{errors?.image}</Form.Control.Feedback>
                    {/* {isUploading && } */}
                </Form.Group>

                {/* 記事投稿日（表示用） */}
                <Form.Group className='mb-3 w-100' controlId='published_date'>
                  <div className='position-relative'>
                    <Form.Label>
                      記事投稿日（表示用）<span className='is-required'>*</span>
                    </Form.Label>
                    <DatePicker
                      selected={publishedDate}
                      id='published_date'
                      placeholderText='Select Date'
                      onChange={(date) => {
                        if (date) setPublishedDate(date);
                        if (!date) setPublishedDate(new Date());
                      }}
                      maxDate={new Date('9999-12-31')}
                      dateFormat='yyyy-MM-dd'
                      className='form-control'
                    />
                  </div>
                </Form.Group>

                {/* Multi section */}
                {items?.map((item, index) => (
                    <AddNewSection
                        key={index}
                        itemIndex={index}
                        handleChange={handleChange}
                        handleAddNewPost={handleAddNewPost}
                        handleRemovePost={handleRemovePost}
                        getImageFullPath={getImageFullPath}
                        item={item}
                        itemTitles={itemTitles}
                        errors={errors.items && errors.items[index]}
                    />
                ))}

                {/* Add new section */}
                {items?.length < caseStudy?.length && (
                    <div className='add-new' onClick={() => handleAddNewSection()}>
                        <img src={PlusIcon} alt='Plus Icon'/>
                    </div>
                )}

                {/* Url Input */}
                <Form.Group className='mb-3' controlId='urlId'>
                    <Form.Label>
                        URL（任意）
                    </Form.Label>
                    <Form.Control type='url' onChange={handleChange} name='url' value={formData?.url}
                                  placeholder='URL...' isInvalid={errors?.url}/>
                    <Form.Control.Feedback type='invalid'>{errors?.url}</Form.Control.Feedback>
                </Form.Group>

                {/* Tag Input */}
                <Form.Group className='mb-3' controlId='control_tag'>
                    <Form.Label>
                        Tag<span className='is-required'>*</span>
                    </Form.Label>
                    <Form.Control hidden/>
                    <div
                        className={formData?.tags?.length ? 'form-tags' : errors?.tags ? 'form-control is-invalid' : 'form-control'}>
                        <ul className='tags'>
                            {formData?.tags?.map((tag, index) => (
                                <li key={index}>
                                    {tag?.title}
                                    <div className='remove' onClick={() => handleRemoveTag(tag)}>
                                        <img src={MiniCross} alt='Cross Icon'/>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <Form.Control.Feedback type='invalid'>{errors?.tags}</Form.Control.Feedback>

                    {/* Tag list initial render */}
                    {tags?.length ? (
                        <ul className='tags'>
                            {loadMore
                                ? tags?.map((tag, index) => (
                                    <li key={index} onClick={() => !tag?.selected && handleSelectTag(tag)}
                                        className={tag?.selected ? 'disabled' : null}>
                                        {tag?.title}
                                    </li>
                                ))
                                : tags?.slice(0, 5)?.map((tag, index) => (
                                    <li key={index} onClick={() => !tag?.selected && handleSelectTag(tag)}
                                        className={tag?.selected ? 'disabled' : null}>
                                        {tag?.title}
                                    </li>
                                ))}
                            <span className='more' onClick={() => setLoadMore((prevProps) => !prevProps)}>
                {loadMore ? 'Less-' : 'More+'}
              </span>
                        </ul>
                    ) : (
                        <div className='no-found py-2'>No tag found</div>
                    )}
                </Form.Group>

                {/* プッシュ通知を送信する Checkbox */}
                <div className='mb-3'>
                    <input type='checkbox' id='notification_flg' name='notification_flg' value={formData?.notification_flg ? formData.notification_flg : 0} checked={formData?.notification_flg ? formData.notification_flg : 0} onChange={handleCheckboxChange}/>
                    <label htmlFor='notification_flg'>プッシュ通知を送信する</label>
                </div>

                {/* Operation user_name */}
                <Form.Group className='mb-3' controlId='user_name'>
                    <Form.Label>
                        担当者名 <span className='is-required'>*</span>
                    </Form.Label>
                    <Form.Control
                        type='text'
                        onChange={handleChange}
                        name='user_name'
                        value={formData?.user_name}
                        placeholder='ご記入ください...'
                        isInvalid={errors?.user_name}
                    />
                    <Form.Control.Feedback type='invalid'>{errors?.user_name}</Form.Control.Feedback>
                </Form.Group>

                {/* Actions Button */}
                <div className='pb-5 pt-2'>
                    <button className='button primary mb-2' onClick={(event) => handlePostPublish(event, 'preview')}>
                        プレビュー
                    </button>
                    <button
                        className='button primary mb-2'
                        type='submit'
                        disabled={isDisabled}
                        onClick={(event) => {
                            setPostStatus('scheduled');
                            handlePostPublish(event, 'submit');
                        }}>
                        掲載予定
                    </button>
                    <button
                        className='button primary'
                        type='submit'
                        onClick={(event) => {
                            setPostStatus('draft');
                            handleDraftPostSubmit(event);
                        }}>
                        下書き保存（非公開）
                    </button>
                </div>
            </Form>

            {/* Server error */}
            {serverError && <Alert variant='danger'>{serverError}</Alert>}

            {/* Schedule  modal */}
            {postStatus && typeParam && (
                <ScheduleModal
                    show={showScheduleModal}
                    payload={formData}
                    postStatus={postStatus}
                    handleClose={() => setShowScheduleModal(false)}
                />
            )}

            {/* Post preview modal */}
            {!errorStatus && postPreviewModal && (
                <PostPreviewModal
                    show={!errorStatus && postPreviewModal}
                    isPreview={true}
                    post={{...formData, original_image: originalImage, type: typeParam, items}}
                    handleClose={() => setPostPreviewModal(false)}
                />
            )}
        </div>
    );
};

MultiPostForm.prototype = {
    postData: Proptypes.object
};
